<template>
  <div class="p-6">
    <div v-if="loading" class="flex justify-center py-8">
      <el-skeleton :rows="3" animated />
    </div>

    <el-alert
      v-else-if="error"
      type="error"
      :title="i18n.t('errorLoadingData')"
      :description="error.message"
      show-icon
      :closable="false">
      <el-button @click="retryFetch">
        {{ i18n.t('retry') }}
      </el-button>
    </el-alert>

    <div
      v-else-if="!personStats || !personStats.tools?.length"
      class="flex flex-col items-center justify-center py-8">
      <EmptyState
        :icon="Wrench"
        :title="i18n.t('browserExtension.noToolsDataTitle')"
        :text="i18n.t('browserExtension.noToolsDataDescription')"
        :show-docs-btn="false" />
    </div>

    <div v-else class="flex h-full flex-col">
      <!-- Summary Card -->
      <div
        class="shadow-sm mb-6 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div class="flex items-center gap-3">
          <div
            class="bg-blue-100 text-blue-600 dark:bg-blue-900/30 flex h-10 w-10 items-center justify-center rounded-full">
            <Wrench class="h-5 w-5" />
          </div>
          <div>
            <div class="text-sm font-medium text-gray-500 dark:text-gray-400">
              {{ i18n.t('browserExtension.uniqueTools') }}
            </div>
            <div class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ personStats.metadata.unique_tools }}
            </div>
          </div>
        </div>
      </div>

      <!-- Tools Table -->
      <SmTable
        :data="personStats.tools"
        :columns="columns"
        :loading="loading"
        key-field="software_name"
        class="flex-1"
        @row-click="handleRowClick">
        <template #software_name="{ row }">
          <div class="font-medium text-gray-900 dark:text-white">
            {{ row.software_name }}
          </div>
        </template>

        <template #total_sessions="{ row }">
          <div class="flex items-center gap-2">
            <span>{{ row.total_sessions }}</span>
            <div
              class="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
              <div
                class="bg-blue-500 h-full rounded-full"
                :style="{
                  width: `${Math.min((row.total_sessions / maxSessions) * 100, 100)}%`,
                  backgroundColor: '#3b82f6',
                }" />
            </div>
          </div>
        </template>

        <template #last_used="{ row }">
          <span class="text-gray-500 dark:text-gray-400">
            {{
              row.last_used ? displayDate(new Date(row.last_used * 1000)) : '-'
            }}
          </span>
        </template>
      </SmTable>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Wrench } from 'lucide-vue-next'
  import EmptyState from '@/components/shared/EmptyState.vue'
  import SmTable from '@/components/sm/SmTable.vue'
  import type { ToolUsage } from '@/client'
  import { displayDate } from '@/utils/formatter/date'
  import { capitalize } from '@/utils/formatter/formatter'
  import type { Column } from '@/components/sm/SmTable.types'
  import {
    PERSON_STATS_KEY,
    type PersonStatsContext,
  } from '@/stores/browserextension/browserextension.store'

  interface Props {
    installationId: string
    fromDate?: Date | null
    tillDate?: Date | null
  }

  defineProps<Props>()
  const emit = defineEmits<{
    (e: 'tool-click', toolName: string): void
  }>()
  const i18n = useI18n()

  // Inject shared person stats from parent
  const injected = inject<PersonStatsContext>(PERSON_STATS_KEY)

  // Use injected data if available, otherwise show loading
  const personStats = computed(() => injected?.personStats.value ?? null)
  const loading = computed(() => injected?.loading.value ?? false)
  const error = computed(() => injected?.error.value ?? null)

  // Handle row click to emit tool-click event
  const handleRowClick = (_index: number, row: ToolUsage) => {
    emit('tool-click', row.software_name)
  }

  const retryFetch = () => {
    injected?.refetch()
  }

  // Computed properties for visual scaling
  const maxSessions = computed(() => {
    if (!personStats.value?.tools?.length) return 1
    return Math.max(...personStats.value.tools.map((t) => t.total_sessions))
  })

  // Table Columns
  const columns = computed<Column<ToolUsage>[]>(() => [
    {
      key: 'software_name',
      label: capitalize(i18n.t('browserExtension.toolName')),
      width: 2,
      sortable: true,
    },
    {
      key: 'total_sessions',
      label: capitalize(i18n.t('browserExtension.sessions')),
      width: 1,
      sortable: true,
    },
    {
      key: 'last_used',
      label: capitalize(i18n.t('lastUsage')),
      width: 1,
      sortable: true,
    },
  ])
</script>
